import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import "./Customizer.css";
import getApiBase from "../utils/apiBase";
import { resolveImageUrl } from "../utils/resolveImageUrl";
import Seo from "../components/Seo";
import CustomYoYoPreview from "../components/CustomYoYoPreview";
import { useCart } from "./CartContext";
import { useAuth } from "./AuthContext";
import Modal from "../components/Modal";
import { useI18n } from "../i18n/I18nContext";

const API_BASE = getApiBase();
const CUSTOM_YOYO_PRODUCT_ID = "custom-logo-yoyo";
const CUSTOM_YOYO_PRODUCT_NAME = "Custom Logo Yo-Yo";
const CUSTOM_YOYO_PRICE = 19.99;
const MAX_UPLOAD_FILE_SIZE_BYTES = 5 * 1024 * 1024;
const ALLOWED_UPLOAD_TYPES = ["image/png", "image/jpeg", "image/webp"];

const readError = async (response, fallback) => {
  await response.json().catch(() => ({}));
  return fallback;
};

const readFileAsDataUrl = (file, errorMessage) => (
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error(errorMessage));
    reader.readAsDataURL(file);
  })
);

const groupedColors = (colors) => (
  colors.reduce((groups, color) => {
    const group = color.group || "Colors";
    if (!groups[group]) groups[group] = [];
    groups[group].push(color);
    return groups;
  }, {})
);

const Customizer = () => {
  const { t, formatNumber } = useI18n();
  const navigate = useNavigate();
  const previewRef = useRef(null);
  const { dispatch } = useCart();
  const { user, loading: authLoading } = useAuth();
  const [colors, setColors] = useState([]);
  const [selectedColorId, setSelectedColorId] = useState("");
  const [loadingColors, setLoadingColors] = useState(true);
  const [artworkFile, setArtworkFile] = useState(null);
  const [artworkPreview, setArtworkPreview] = useState("");
  const [artworkUrl, setArtworkUrl] = useState("");
  const [previewDataUrl, setPreviewDataUrl] = useState("");
  const [previewDraft, setPreviewDraft] = useState({});
  const [quantity, setQuantity] = useState("2");
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [contactConfig, setContactConfig] = useState(null);
  const [modalConfig, setModalConfig] = useState(null);
  const [uploadingArtwork, setUploadingArtwork] = useState(false);
  const [addingToCart, setAddingToCart] = useState(false);

  const selectedColor = useMemo(
    () => colors.find((color) => color.id === selectedColorId) || colors[0] || null,
    [colors, selectedColorId],
  );
  const colorGroups = useMemo(() => groupedColors(colors), [colors]);
  const selectedGroupName = selectedColor?.group || Object.keys(colorGroups)[0] || "";
  const visibleColors = colorGroups[selectedGroupName] || colors;
  const unitPrice = formatNumber(CUSTOM_YOYO_PRICE, { style: "currency", currency: "USD" });
  const faqItems = useMemo(() => [
    { question: t("customizer.faq.previewQuestion"), answer: t("customizer.faq.previewAnswer") },
    { question: t("customizer.faq.uploadQuestion"), answer: t("customizer.faq.uploadAnswer") },
    { question: t("customizer.faq.formatQuestion"), answer: t("customizer.faq.formatAnswer") },
    { question: t("customizer.faq.priceQuestion"), answer: t("customizer.faq.priceAnswer", { price: unitPrice }) },
    { question: t("customizer.faq.positionQuestion"), answer: t("customizer.faq.positionAnswer") },
    { question: t("customizer.faq.reviewQuestion"), answer: t("customizer.faq.reviewAnswer") },
  ], [t, unitPrice]);
  const displayGroupName = (groupName) => (
    groupName === "Colors" ? t("customizer.color.groupFallback") : groupName
  );

  const siteUrl = typeof window !== "undefined" ? window.location.origin : "https://ych-yoyo.com";
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqItems.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };
  const pageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: t("customizer.title"),
    url: `${siteUrl}/customizer`,
    description: t("customizer.schemaDescription"),
  };

  useEffect(() => {
    let isMounted = true;
    const loadColors = async () => {
      setLoadingColors(true);
      try {
        const response = await fetch(`${API_BASE}/api/customizer/colors`);
        if (!response.ok) {
          throw new Error(await readError(response, t("customizer.error.loadColors")));
        }
        const data = await response.json();
        const items = Array.isArray(data?.items) ? data.items : [];
        if (!isMounted) return;
        setColors(items);
        setSelectedColorId((current) => current || items[0]?.id || "");
      } catch {
        if (isMounted) {
          setError(t("customizer.error.loadColors"));
        }
      } finally {
        if (isMounted) {
          setLoadingColors(false);
        }
      }
    };
    loadColors();
    return () => {
      isMounted = false;
    };
  }, [t]);

  useEffect(() => {
    let isMounted = true;
    const loadContactConfig = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/customizer/contact-config`);
        if (!response.ok) return;
        const data = await response.json();
        if (isMounted) {
          setContactConfig(data);
        }
      } catch (contactError) {
        if (isMounted) {
          setContactConfig(null);
        }
      }
    };

    loadContactConfig();
    return () => {
      isMounted = false;
    };
  }, []);

  const uploadArtworkFile = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    let response;
    try {
      response = await fetch(`${API_BASE}/api/customizer/upload-artwork`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });
    } catch {
      throw new Error(t("customizer.error.uploadArtwork"));
    }
    if (!response.ok) {
      throw new Error(await readError(response, t("customizer.error.uploadArtwork")));
    }
    return response.json();
  };

  const uploadPreviewImage = async (image) => {
    let response;
    try {
      response = await fetch(`${API_BASE}/api/customizer/upload-preview`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ image }),
      });
    } catch {
      throw new Error(t("customizer.error.uploadPreview"));
    }
    if (!response.ok) {
      throw new Error(await readError(response, t("customizer.error.uploadPreview")));
    }
    return response.json();
  };

  const handleArtworkClick = (event) => {
    if (authLoading) {
      event.preventDefault();
      return;
    }
    if (!user) {
      event.preventDefault();
      openLoginModal();
    }
  };

  const handleArtworkChange = async (event) => {
    const file = event.target.files?.[0];
    setError("");
    setSuccessMessage("");
    setPreviewDataUrl("");
    setArtworkUrl("");

    if (!file) return;
    if (!user) {
      event.target.value = "";
      openLoginModal();
      return;
    }
    if (!ALLOWED_UPLOAD_TYPES.includes(file.type)) {
      setError(t("customizer.error.invalidType"));
      event.target.value = "";
      return;
    }
    if (file.size > MAX_UPLOAD_FILE_SIZE_BYTES) {
      setError(t("customizer.error.tooLarge"));
      event.target.value = "";
      return;
    }

    setArtworkFile(file);
    setUploadingArtwork(true);
    try {
      const [dataUrl, uploadResult] = await Promise.all([
        readFileAsDataUrl(file, t("customizer.error.readFile")),
        uploadArtworkFile(file),
      ]);
      setArtworkPreview(dataUrl);
      setArtworkUrl(uploadResult.url || "");
    } catch (uploadError) {
      setArtworkFile(null);
      setArtworkPreview("");
      setArtworkUrl("");
      setError(uploadError.message || t("customizer.error.uploadArtwork"));
      event.target.value = "";
    } finally {
      setUploadingArtwork(false);
    }
  };

  const openLoginModal = () => {
    setModalConfig({
      type: "login",
      title: t("customizer.login.title"),
      message: t("customizer.login.message"),
    });
  };

  const openMessageModal = (title, message) => {
    setModalConfig({ type: "message", title, message });
  };

  const goToAuthPage = (path) => {
    const nextPath = `${window.location.pathname}${window.location.search}` || "/customizer";
    setModalConfig(null);
    navigate(`${path}?next=${encodeURIComponent(nextPath)}`);
  };

  const handleAddToCart = async () => {
    setError("");
    setSuccessMessage("");
    if (authLoading) {
      return;
    }
    if (!user) {
      openLoginModal();
      return;
    }
    if (!selectedColor) {
      setError(t("customizer.error.chooseColor"));
      return;
    }
    if (!artworkPreview) {
      openMessageModal(t("customizer.error.uploadFirstTitle"), t("customizer.error.uploadFirstMessage"));
      return;
    }
    if (uploadingArtwork) {
      setError(t("customizer.error.uploading"));
      return;
    }
    if (!artworkUrl) {
      setError(t("customizer.error.uploadIncomplete"));
      return;
    }
    const selectedQuantity = Number(quantity);
    if (!Number.isFinite(selectedQuantity) || selectedQuantity < 1) {
      setError(t("customizer.error.quantity"));
      return;
    }

    setAddingToCart(true);
    try {
      const generatedPreview = previewRef.current?.generatePreview();
      const draft = previewRef.current?.getDraft() || previewDraft;
      if (!generatedPreview) {
        setError(t("customizer.error.generatePreview"));
        return;
      }

      const uploadedPreview = await uploadPreviewImage(generatedPreview);
      const uploadedPreviewUrl = uploadedPreview.url || "";
      if (!uploadedPreviewUrl) {
        throw new Error(t("customizer.error.uploadPreview"));
      }

      dispatch({
        type: "ADD_ITEM",
        payload: {
          id: CUSTOM_YOYO_PRODUCT_ID,
          itemKey: `custom-${Date.now()}`,
          type: "custom_yoyo",
          name: CUSTOM_YOYO_PRODUCT_NAME,
          price: CUSTOM_YOYO_PRICE,
          quantity: selectedQuantity,
          selectedColor: selectedColor.name,
          customColorId: selectedColor.id,
          customColorName: selectedColor.name,
          customColorGroup: selectedColor.group,
          customColorImageUrl: selectedColor.imageUrl,
          artworkUrl,
          previewImageUrl: uploadedPreviewUrl,
          imageUrl: uploadedPreviewUrl,
          previewDraft: {
            selectedColorId: selectedColor.id,
            selectedColorName: selectedColor.name,
            selectedColorImageUrl: selectedColor.imageUrl,
            ...draft,
          },
        },
      });
      setPreviewDataUrl(generatedPreview);
      setSuccessMessage("customizer.cart.success");
    } catch (addError) {
      setError(addError.message || t("customizer.error.addFailed"));
    } finally {
      setAddingToCart(false);
    }
  };

  const progressSteps = [
    { key: "color", label: t("customizer.progress.color"), complete: Boolean(selectedColor) },
    { key: "artwork", label: t("customizer.progress.artwork"), complete: Boolean(artworkPreview) },
    { key: "preview", label: t("customizer.progress.preview"), complete: Boolean(previewDataUrl) },
    { key: "cart", label: t("customizer.progress.cart"), complete: Boolean(successMessage) },
  ];

  return (
    <main className="customizer-page">
      <Seo
        title={t("customizer.title")}
        description={t("customizer.seoDescription")}
        path="/customizer"
        image="/images/diy.png"
        structuredData={[pageSchema, faqSchema]}
      />

      <section className="customizer-hero">
        <div className="customizer-hero-copy">
          <p className="customizer-eyebrow">{t("customizer.heroEyebrow")}</p>
          <h1>{t("customizer.title")}</h1>
          <p>{t("customizer.heroBody")}</p>
          <a className="customizer-hero-cta" href="#custom-yoyo-builder">
            {t("customizer.start")}
          </a>
        </div>
        <div className="customizer-hero-visual" aria-hidden="true">
          <img src={resolveImageUrl(selectedColor?.imageUrl || "/images/diy.png")} alt="" />
        </div>
      </section>

      <section className="customizer-progress" aria-label={t("customizer.progressAria")}>
        {progressSteps.map((step, index) => (
          <div className={`progress-step${step.complete ? " complete" : ""}`} key={step.key}>
            <span>{index + 1}</span>
            <strong>{step.label}</strong>
          </div>
        ))}
      </section>

      <section className="customizer-builder" id="custom-yoyo-builder">
        <section className="customizer-equation-builder">
          <div className="equation-panel color-panel">
            <div className="step-heading">
              <span>{t("customizer.step", { number: 1 })}</span>
              <h2>{t("customizer.color.title")}</h2>
              <p>{t("customizer.color.description")}</p>
            </div>

            {loadingColors ? (
              <div className="customizer-loading">{t("customizer.color.loading")}</div>
            ) : (
              <div className="label-color-groups">
                <div className="label-group-tabs" role="tablist" aria-label={t("customizer.color.groupsAria")}>
                  {Object.keys(colorGroups).map((groupName) => (
                    <button
                      type="button"
                      key={groupName}
                      className={groupName === selectedGroupName ? "active" : ""}
                      onClick={() => {
                        const firstColor = colorGroups[groupName]?.[0];
                        if (firstColor) {
                          setSelectedColorId(firstColor.id);
                          setPreviewDataUrl("");
                          setSuccessMessage("");
                        }
                      }}
                    >
                      {displayGroupName(groupName)}
                    </button>
                  ))}
                </div>
                <div className="selected-color-stage">
                  <img src={resolveImageUrl(selectedColor?.imageUrl)} alt={selectedColor?.name || t("customizer.color.selectedAlt")} />
                  <strong>{selectedColor?.name || t("customizer.color.selectedFallback")}</strong>
                </div>
                <div className="label-color-scroll" role="list">
                  {visibleColors.map((color) => (
                    <button
                      type="button"
                      className={`label-color-option${selectedColor?.id === color.id ? " selected" : ""}`}
                      key={color.id}
                      onClick={() => {
                        setSelectedColorId(color.id);
                        setPreviewDataUrl("");
                        setSuccessMessage("");
                      }}
                      title={color.name}
                      aria-label={t("customizer.color.chooseAria", { color: color.name })}
                    >
                      <img src={resolveImageUrl(color.imageUrl)} alt="" />
                      <span>{color.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="equation-symbol">+</div>

          <div className="equation-panel artwork-panel">
            <div className="step-heading">
              <span>{t("customizer.step", { number: 2 })}</span>
              <h2>{t("customizer.artwork.title")}</h2>
              <p>{t("customizer.artwork.description")}</p>
            </div>

            <label className="artwork-upload">
              <input
                className="artwork-upload-input"
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onClick={handleArtworkClick}
                onChange={handleArtworkChange}
              />
              <span className="artwork-upload-picker" aria-hidden="true">{t("customizer.artwork.chooseFile")}</span>
              <span>
                <strong>{artworkFile ? artworkFile.name : t("customizer.artwork.noFile")}</strong>
                <small>{t("customizer.artwork.fileHelp")}</small>
              </span>
            </label>

            <div className="artwork-stage">
                {artworkPreview ? <img src={artworkPreview} alt={t("customizer.artwork.uploadedAlt")} /> : <span className="empty-artwork">{t("customizer.artwork.empty")}</span>}
                <span>{uploadingArtwork ? t("customizer.artwork.uploading") : artworkUrl ? t("customizer.artwork.uploaded") : artworkFile ? t("customizer.artwork.yours") : t("customizer.artwork.upload")}</span>
            </div>
          </div>

          <div className="equation-symbol">=</div>

          <div className="equation-panel preview-panel">
            <div className="step-heading">
              <span>{t("customizer.step", { number: 3 })}</span>
              <h2>{t("customizer.preview.title")}</h2>
              <p>{t("customizer.preview.description")}</p>
            </div>
            <CustomYoYoPreview
              ref={previewRef}
              baseImage={resolveImageUrl(selectedColor?.imageUrl)}
              uploadedImage={artworkPreview}
              mockupConfig={selectedColor?.mockupConfig}
              onDraftChange={setPreviewDraft}
              onPreviewGenerated={(dataUrl) => {
                setPreviewDataUrl(dataUrl);
                setSuccessMessage("");
              }}
            />
          </div>
        </section>

        <div className="customizer-steps">
          <section className="customizer-step submit-step">
            <div className="step-heading">
              <span>{t("customizer.step", { number: 4 })}</span>
              <h2>{t("customizer.cart.title")}</h2>
              <p>{t("customizer.cart.price", { price: unitPrice })}</p>
            </div>

            <div className="custom-request-form">
              <div className="form-grid">
                <label>
                  {t("customizer.cart.quantity")}
                  <input
                    type="number"
                    min="1"
                    value={quantity}
                    onChange={(event) => setQuantity(event.target.value)}
                  />
                </label>
                <label>
                  {t("customizer.cart.selectedColor")}
                  <input type="text" value={selectedColor?.name || ""} readOnly />
                </label>
              </div>

              {error && <p className="customizer-message error">{error}</p>}
              {successMessage && (
                <p className="customizer-message success">
                  {t(successMessage)} <Link to="/cart">{t("customizer.cart.view")}</Link>
                </p>
              )}

              <button
                type="button"
                className="submit-custom-request"
                data-analytics-target="customizer.add_to_cart"
                onClick={handleAddToCart}
                disabled={authLoading || uploadingArtwork || addingToCart}
              >
                {authLoading ? t("customizer.cart.checking") : addingToCart ? t("customizer.cart.adding") : t("customizer.cart.title")}
              </button>
            </div>

            {contactConfig && (
              <div className="customizer-contact-options">
                <strong>{t("customizer.help.title")}</strong>
                {contactConfig.weworkCustomerServiceEnabled && contactConfig.weworkCustomerServiceUrl ? (
                  <a href={contactConfig.weworkCustomerServiceUrl} target="_blank" rel="noreferrer">
                    {t("customizer.help.wecom")}
                  </a>
                ) : null}
                {contactConfig.email ? <a href={`mailto:${contactConfig.email}`}>{contactConfig.email}</a> : null}
                {contactConfig.whatsapp ? <a href={`https://wa.me/${contactConfig.whatsapp.replace(/\D/g, "")}`}>WhatsApp</a> : null}
                {contactConfig.instagram ? <a href={contactConfig.instagram} target="_blank" rel="noreferrer">Instagram</a> : null}
                {contactConfig.wechat ? <span>{t("customizer.help.wechat", { account: contactConfig.wechat })}</span> : null}
              </div>
            )}
          </section>
        </div>
      </section>

      <section className="customizer-seo-content">
        <div>
          <h2>{t("customizer.content.previewTitle")}</h2>
          <p>{t("customizer.content.previewBody")}</p>
        </div>

        <div>
          <h2>{t("customizer.content.usesTitle")}</h2>
          <p>{t("customizer.content.usesBody")}</p>
        </div>

        <div>
          <h2>{t("customizer.content.processTitle")}</h2>
          <ol>
            <li>{t("customizer.content.process1")}</li>
            <li>{t("customizer.content.process2", { price: unitPrice })}</li>
            <li>{t("customizer.content.process3")}</li>
            <li>{t("customizer.content.process4")}</li>
            <li>{t("customizer.content.process5")}</li>
          </ol>
        </div>

        <div>
          <h2>{t("customizer.content.guidelinesTitle")}</h2>
          <p>{t("customizer.content.guidelinesBody")}</p>
        </div>
      </section>

      <section className="customizer-faq">
        <h2>{t("customizer.faqTitle")}</h2>
        <div className="faq-grid">
          {faqItems.map((item) => (
            <details key={item.question}>
              <summary>{item.question}</summary>
              <p>{item.answer}</p>
            </details>
          ))}
        </div>
      </section>

      {modalConfig ? (
        <Modal
          title={modalConfig.title}
          message={modalConfig.message}
          onClose={() => setModalConfig(null)}
          primaryAction={modalConfig.type === "login" ? {
            label: t("customizer.login.action"),
            onClick: () => goToAuthPage("/login"),
          } : {
            label: t("customizer.modal.ok"),
            onClick: () => setModalConfig(null),
          }}
          secondaryAction={modalConfig.type === "login" ? {
            label: t("customizer.login.create"),
            onClick: () => goToAuthPage("/register"),
          } : null}
        />
      ) : null}
    </main>
  );
};

export default Customizer;
